import { z } from "zod";

/**
 * How a box's weights are chosen, ported from boxed-backend's `boxWeighting`
 * (`weightingType` + `margin` + the two dynamic entries on the box row).
 *
 * The config lives on `boxes.weighting` and is copied onto every `box_versions`
 * row at write, so a past version replays with the config that produced it.
 * `MANUAL` is what every box authored before the port holds: the editor sets
 * each weight itself and the processor leaves them alone.
 */

/** Boxed's default margin for a target-weighted box, in percentage points. */
export const DEFAULT_BOX_MARGIN_PERCENT = 15;

/**
 * Names one authored drop-table entry. `PRICE_TARGET` needs two of them: the
 * pair whose weights the processor moves to hit the target price.
 */
export const entryRefSchema = z.discriminatedUnion("source", [
  z.object({ source: z.literal("CATALOG"), id: z.uuid() }),
  z.object({ source: z.literal("MARKETPLACE"), id: z.string().min(1) }),
]);

export type EntryRef = z.infer<typeof entryRefSchema>;

/**
 * The share of expected value the house keeps, in percentage points. The processor prices a
 * box at a win rate of `1 - marginPercent / 100`, which has to stay above zero; a margin below
 * zero would price the box under what it pays out.
 */
const marginPercentSchema = z.number().min(0).lt(100);

export const weightingSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("MANUAL") }),
  z.object({ type: z.literal("WEIGHT_TARGET"), marginPercent: marginPercentSchema }),
  z.object({
    type: z.literal("PRICE_TARGET"),
    marginPercent: marginPercentSchema,
    dynamicItems: z.tuple([entryRefSchema, entryRefSchema]),
  }),
]);

export type Weighting = z.infer<typeof weightingSchema>;

/** What a box holds until an editor picks a target mode. */
export const MANUAL_WEIGHTING: Weighting = { type: "MANUAL" };
